// src/components/PrototypeCard.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, IconButton, Tooltip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import ModeCommentIcon from '@mui/icons-material/ModeComment';
import DeleteIcon from '@mui/icons-material/Delete';

export default function PrototypeCard({ proto, onDelete, onEdit }) {
  // Handle both data structures
  const chatId = proto.chatId || proto.chat_id;
  const owner = proto.owner || proto.username;
  const thumbnailUrl = proto.thumbnailUrl;
  const updatedAt = proto.updatedAt;
  const ai_description = proto.ai_description;
  const user_prompt = proto.user_prompt;
  const to = `/${owner}/${chatId}`;

  // Clean, sentence-cased title
  const rawTitle = ai_description || 'Untitled Prototype';
  const stripped = rawTitle
    .replace(/^Changes:\s*/i, '')
    .replace(/^(Created an?\s+)/i, '')
    .trim();
  const title = stripped
    ? stripped.charAt(0).toUpperCase() + stripped.slice(1)
    : 'Untitled Prototype';

  // Date formatting
  const dateStr = updatedAt ? new Date(updatedAt).toLocaleDateString() : '';

  const navigate = useNavigate();

  return (
    <Box
      component={Link}
      to={to}
      sx={{
        position: 'relative',
        backgroundColor: 'rgba(255,255,255,.7)',
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        border: '1px solid #ddd',
        borderRadius: 1,
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'box-shadow .15s',
        '&:hover': {
          boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
          border: '1px solid #333'
        },
        '&:hover .icon-overlay': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      {/* action-icon overlay at bottom */}
      {onDelete && onEdit && (
        <Box
          className="icon-overlay"
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '100%',
            backgroundColor: 'rgba(255,255,255,0.9)',
            display: 'flex',
            justifyContent: 'space-around',
            py: 0.5,
            px: 1,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.15s, visibility 0.15s',
            zIndex: 2,
          }}
        >
          <Tooltip title="View" placement="bottom" arrow>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                navigate(to);
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Code" placement="bottom" arrow>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                navigate(to, { state: { initialViewMode: 'code' } });
              }}
            >
              <CodeIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Edit" placement="bottom" arrow>
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                onEdit(chatId);
              }}
            >
              <ModeCommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete" placement="bottom" arrow>
            <IconButton
              size="small"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                onDelete(chatId);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* thumbnail */}
      <Box sx={{ height: 160, background: '#f3f3f3' }}>
        {thumbnailUrl ? (
          <Box
            component="img"
            src={thumbnailUrl}
            alt={title}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 32,
              color: '#bbb',
            }}
          >
            🗂
          </Box>
        )}
      </Box>

      {/* title, prompt, meta */}
      <Box sx={{ p: 2 }}>
        <Box sx={{
            fontWeight: 600,
            fontSize: 15,
            mb: 0.5,
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 3,
            overflow: 'hidden',

          }}>{title}</Box>
        {user_prompt && (
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary', mb: 1, whiteSpace: 'nowrap',textOverflow: 'ellipsis', overflow: 'hidden' }}>
            {user_prompt}
          </Box>
        )}
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {dateStr}
          {proto.remix_count > 0 && ` • ${proto.remix_count} remixes`}
        </Box>
      </Box>
    </Box>
  );
}
